import React, { useState, useEffect, useRef } from "react";
import {
  X,
  ShoppingBag,
  ArrowRight,
  Trash2,
  Plus,
  Minus,
  Check,
  CheckCircle,
  Truck,
  Loader2,
  Bot,
  Locate,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  RefreshCcw,
  Menu as MenuIcon, // Kita gunakan alias karena 'Menu' mungkin bentrok dengan nama komponen lain
  Clock,
} from "lucide-react";
import {
  RESTAURANT_INFO,
  BRANCHES,
  PAYMENT_METHODS,
  LOGO_URL,
  fetchDynamicData,
  CSV_URL,
} from "./constants";
import {
  MenuItem,
  CartItem,
  OrderDetails,
  Branch,
  NotificationType,
  CartAddon,
} from "./types";

import Papa from "papaparse";

// Import refactored components
import MenuView from "./pages/Menu";
import Footer from "./section/Footer";
import { HeroSlider } from "./HeroPromos";

// ✅ Tambahkan React. di depan fungsi lazy
const GaleriView = React.lazy(() => import("./pages/Galeri"));
const AduanView = React.lazy(() => import("./pages/Aduan"));
const LocationView = React.lazy(() => import("./pages/Lokasi"));
const PanduanView = React.lazy(() => import("./pages/Panduan"));
const HistoryView = React.lazy(() => import("./pages/History"));
const OrderReceipt = React.lazy(() => import("./OrderReceipt"));
const DataPengiriman = React.lazy(() => import("./DataPengiriman"));

// Import new sections
import Cabang from "./section/cabang";
import Rekomendasi from "./section/rekomendasi";
import Kelebihan from "./section/kelebihan";
import Operasional from "./section/operasional";
import Pertanyaan from "./section/pertanyaan";
import Saran from "./section/saran";

import { HelmetProvider } from "react-helmet-async";

// Rumus Haversine (Garis Lurus)
export const getDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const getOperatingStatus = () => {
  const now = new Date();
  const hour = now.getHours();
  const minute = now.getMinutes();
  const currentTime = hour + minute / 60;
  const openingTime = 7;
  const closingTime = 21.5;

  if (currentTime >= openingTime && currentTime < closingTime) {
    return {
      label: "Buka Sekarang",
      color: "bg-green-600",
      icon: <CheckCircle size={15} />,
    };
  } else if (currentTime >= openingTime - 1 && currentTime < openingTime) {
    return {
      label: "Segera Buka",
      color: "bg-amber-600",
      icon: <Clock size={14} />,
    };
  } else {
    return { label: "Tutup", color: "bg-rose-600", icon: <X size={14} /> };
  }
};

export const SectionHeading: React.FC<{
  title: string;
  subtitle?: string;
  centered?: boolean;
}> = ({ title, subtitle, centered = true }) => (
  <header
    className={`${
      centered ? "text-center" : "text-left"
    } mb-10 md:mb-16 px-4 space-y-3 md:space-y-5 animate-fade-in`}
  >
    <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold tracking-tight text-slate-900">
      {title}
    </h2>
    {subtitle && (
      <p className="text-slate-600 text-sm sm:text-base md:text-xl max-w-2xl mx-auto leading-relaxed font-medium">
        {subtitle}
      </p>
    )}
    <div
      className={`h-1 w-12 md:w-20 bg-[#ee1d23] rounded-full ${
        centered ? "mx-auto" : ""
      }`}
      aria-hidden="true"
    ></div>
  </header>
);

const ActiveOrderPreview: React.FC<{
  count: number;
  total: number;
  onClick: () => void;
}> = ({ count, total, onClick }) => {
  if (count === 0) return null;
  return (
    <div className="fixed bottom-8 left-4 md:left-8 z-30 animate-fade-in-up">
      <button
        onClick={onClick}
        aria-label={`Lihat keranjang belanja: ${count} item, total Rp ${total.toLocaleString()}`}
        className="bg-white border-2 border-[#ee1d23] text-slate-900 px-4 py-3 rounded-2xl shadow-2xl flex items-center gap-4 hover:scale-105 transition-all active:scale-95 group min-h-[52px]"
      >
        <div className="w-10 h-10 bg-[#ee1d23] text-white rounded-xl flex items-center justify-center group-hover:rotate-12 transition-transform">
          <ShoppingBag size={20} />
        </div>
        <div className="text-left">
          <p className="text-[10px] font-black uppercase text-[#ee1d23] leading-none mb-1">
            Pesanan Aktif
          </p>
          <p className="text-sm font-black text-slate-900 leading-none">
            {count} Item • Rp {total.toLocaleString()}
          </p>
        </div>
      </button>
    </div>
  );
};

const HomeView: React.FC<{
  onNavigate: (view: string, branchId?: number) => void;
  onAddToCart: (item: MenuItem, addons?: MenuItem[]) => void;
  onOpenOnlineModal: () => void;
  onOpenDetail: (item: MenuItem) => void;
  userCoords: { lat: number; lng: number } | null;
  setUserCoords: (coords: { lat: number; lng: number }) => void;
  onRequestLocation: () => void;
  isLocating: boolean;
}> = ({
  onNavigate,
  onAddToCart,
  onOpenOnlineModal,
  onOpenDetail,
  userCoords,
  onRequestLocation,
  isLocating,
}) => {
  const [isMobile, setIsMobile] = useState(false);

  const [allMenuItems, setAllMenuItems] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${CSV_URL}&t=${Date.now()}`);
        const csvText = await response.text();

        Papa.parse(csvText, {
          header: false,
          skipEmptyLines: true,
          complete: (results) => {
            const rows = results.data as any[][];
            // Mulai dari index 1 untuk melewati header
            const parsedData = rows
              .slice(1)
              .filter((row) => row[2]) // Pastikan Kolom Nama Menu (index 2) tidak kosong
              .map((row) => ({
                id: row[0],
                category: row[1],
                name: row[2],
                description: row[3],
                priceNum: parseInt(
                  row[4]?.toString().replace(/\D/g, "") || "0"
                ),
                tag: row[5],
                image: row[6],
              }));
            setAllMenuItems(parsedData);
          },
        });
      } catch (err) {
        console.error("Fetch error:", err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    // Buat media query list untuk mobile (max-width: 767px)
    const mql = window.matchMedia("(max-width: 767px)");

    // Set state awal berdasarkan kondisi saat ini
    setIsMobile(mql.matches);

    // Listener yang hanya jalan saat status "matches" berubah (True ke False atau sebaliknya)
    const handleChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
    };

    // Tambahkan listener
    mql.addEventListener("change", handleChange);

    // Cleanup: hapus listener saat komponen di-unmount
    return () => mql.removeEventListener("change", handleChange);
  }, []);
  return (
    <div className="animate-fade-in">
      <section
        className="relative min-h-screen flex items-center pt-24 md:pt-28 bg-slate-50"
        aria-labelledby="hero-title"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
          <div className="space-y-6 md:space-y-8 text-center lg:text-left py-10 md:py-20">
            <h1
              id="hero-title"
              className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-serif font-bold tracking-tight leading-[1.1] text-slate-900"
            >
              Welcome to <br className="hidden sm:block" />
              <span className="text-[#ee1d23]">Hisana Padang!</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl leading-relaxed max-w-xl mx-auto lg:mx-0 text-slate-700 font-medium">
              Nikmati ayam goreng krispi pilihan keluarga dengan bumbu rahasia
              yang meresap hingga ke tulang.
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 sm:gap-5 justify-center lg:justify-start pt-4">
              <button
                onClick={() => onNavigate("menu")}
                className="bg-[#ee1d23] text-white px-8 py-4 rounded-2xl font-black shadow-xl hover:bg-[#d1191e] hover:-translate-y-1 transition-all flex items-center justify-center gap-3 text-xs sm:text-sm min-h-[52px]"
              >
                LIHAT MENU <ArrowRight size={18} />
              </button>
              <button
                onClick={onOpenOnlineModal}
                className="bg-white text-[#ee1d23] border-2 border-[#ee1d23] px-8 py-4 rounded-2xl font-black shadow-lg hover:bg-rose-50 hover:-translate-y-1 transition-all flex items-center justify-center gap-3 text-xs sm:text-sm min-h-[52px]"
              >
                <ShoppingBag size={18} /> PESAN ONLINE
              </button>
            </div>
          </div>
          <div className="relative p-2 sm:p-4 md:p-8 order-first lg:order-last">
            <HeroSlider />
          </div>
        </div>
      </section>

      <Cabang
        onNavigate={onNavigate}
        userCoords={userCoords}
        onRequestLocation={onRequestLocation}
        isLocating={isLocating}
        isMobile={isMobile}
      />

      <Rekomendasi
        allMenuItems={allMenuItems}
        onAddToCart={onAddToCart}
        onOpenDetail={onOpenDetail}
      />

      <Kelebihan />

      <Operasional />

      <Pertanyaan />

      <Saran />
    </div>
  );
};

const App: React.FC = () => {
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [notification, setNotification] = useState<NotificationType | null>(
    null
  );
  // --- Pasang ini di dalam fungsi komponen induk kamu (bagian atas bersama state lain) ---
  const [loadChatBot, setLoadChatBot] = useState(false);
  const ChatBotRef = useRef<any>(null);

  useEffect(() => {
    const triggerChatBotLoad = () => {
      if (!loadChatBot) {
        // Browser baru akan menembak request unduhan file Chatbot saat user mulai menyentuh layar
        ChatBotRef.current = React.lazy(() => import("./ChatBot"));
        setLoadChatBot(true);
      }
      // Bersihkan listener agar hemat performa RAM device user
      window.removeEventListener("scroll", triggerChatBotLoad);
      window.removeEventListener("touchstart", triggerChatBotLoad);
      window.removeEventListener("click", triggerChatBotLoad);
    };

    // Pantau pergerakan asli manusia (scroll, sentuhan jari di HP, atau klik)
    window.addEventListener("scroll", triggerChatBotLoad, { passive: true });
    window.addEventListener("touchstart", triggerChatBotLoad, {
      passive: true,
    });
    window.addEventListener("click", triggerChatBotLoad, { passive: true });

    return () => {
      window.removeEventListener("scroll", triggerChatBotLoad);
      window.removeEventListener("touchstart", triggerChatBotLoad);
      window.removeEventListener("click", triggerChatBotLoad);
    };
  }, [loadChatBot]);
  const [view, setView] = useState(() => {
    // 1. Cek Pathname di URL (misal: hisanapadang.com/menu)
    const path = window.location.pathname.replace("/", "");

    // Validasi agar path yang aneh-aneh tidak masuk ke state
    const validViews = ["beranda", "menu", "galeri", "kontak"];
    if (path && validViews.includes(path)) return path;

    // 2. Jika Path kosong/root, cek LocalStorage
    const savedView = localStorage.getItem("hisana_last_view");
    if (savedView) return savedView;

    return "beranda";
  });

  const [targetBranchId, setTargetBranchId] = useState<number | undefined>(
    undefined
  );
  const [addons, setAddons] = useState<MenuItem[]>([]);
  const [viewingItem, setViewingItem] = useState<MenuItem | null>(null);

  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const savedCart = localStorage.getItem("hisana_cart_storage");
      if (!savedCart) return [];
      const parsed = JSON.parse(savedCart);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const [history, setHistory] = useState<OrderDetails[]>(() => {
    try {
      const saved = localStorage.getItem("hisana_history");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("hisana_history", JSON.stringify(history));
  }, [history]);

  useEffect(() => {
    // 1. Simpan ke LocalStorage agar refresh aman
    localStorage.setItem("hisana_last_view", view);

    // 2. Update URL Pathname (bukan Hash lagi)
    const currentPath = window.location.pathname.replace("/", "");
    const targetPath = view === "beranda" ? "" : view;

    // Cek supaya tidak push state berulang kali jika sudah di URL yang sama
    if (currentPath !== targetPath) {
      const newUrl = view === "beranda" ? "/" : `/${view}`;
      window.history.pushState({ view }, "", newUrl);
    }
  }, [view]);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // Ambil view dari state history (jika ada) atau dari pathname
      const pathFromUrl =
        window.location.pathname.replace("/", "") || "beranda";

      // Sinkronkan state React dengan URL baru
      if (pathFromUrl !== view) {
        setView(pathFromUrl);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [view]);

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showOnlineModal, setShowOnlineModal] = useState(false);
  const [activeReceipt, setActiveReceipt] = useState<OrderDetails | null>(null);
  const [userCoords, setUserCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  // Di dalam App.tsx, sebelum customerInfo
  const [branches, setBranches] = useState<Branch[]>(BRANCHES);

  // Tambahkan useEffect untuk update data dari Sheets saat load
  useEffect(() => {
    const loadData = async () => {
      const dataDinamis = await fetchDynamicData();
      setBranches(dataDinamis);

      // Opsional: Jika ingin selectedBranch otomatis terupdate setelah fetch
      setCustomerInfo((prev) => ({
        ...prev,
        selectedBranch:
          dataDinamis.find((b) => b.id === prev.selectedBranch.id) ||
          dataDinamis[0],
      }));
    };
    loadData();
  }, []);

  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    phone: "",
    address: "",
    patokan: "",
    deliveryMode: "jemput" as "antar" | "jemput",
    shippingCost: 0,
    selectedBranch: BRANCHES[0] as Branch,
    paymentChoice: "qris" as "qris" | "bank_bsi",
  });

  const [roadDistance, setRoadDistance] = useState<number | null>(null);
  const [isCalculatingDistance, setIsCalculatingDistance] = useState(false);
  const [allBranchDistances, setAllBranchDistances] = useState<{
    [key: number]: number;
  }>({});
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [generatedOrder, setGeneratedOrder] = useState<OrderDetails | null>(
    null
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const watchId = useRef<number | null>(null);
  const hasManuallySelectedBranch = useRef<boolean>(false);
  const geocodeController = useRef<AbortController | null>(null);

  useEffect(() => {
    let frameId: number;
    const checkMobile = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        setIsMobile(window.innerWidth < 768);
      });
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => {
      window.removeEventListener("resize", checkMobile);
      cancelAnimationFrame(frameId);
      if (watchId.current !== null)
        navigator.geolocation.clearWatch(watchId.current);
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("hisana_cart_storage", JSON.stringify(cart));
    } catch {}
  }, [cart]);

  useEffect(() => {
    const calculateDrivingDistance = async () => {
      const currentBranchId = customerInfo.selectedBranch.id;
      if (!userCoords) return;

      if (allBranchDistances[currentBranchId]) {
        setRoadDistance(allBranchDistances[currentBranchId]);
        return;
      }

      setIsCalculatingDistance(true);
      try {
        const start = `${userCoords.lng},${userCoords.lat}`;
        const end = `${customerInfo.selectedBranch.lng},${customerInfo.selectedBranch.lat}`;
        const response = await fetch(
          `https://router.project-osrm.org/route/v1/driving/${start};${end}?overview=false`
        );
        const data = await response.json();

        let distance = 0;
        if (data.routes && data.routes.length > 0) {
          distance = data.routes[0].distance / 1000;
        } else {
          distance = getDistance(
            userCoords.lat,
            userCoords.lng,
            customerInfo.selectedBranch.lat,
            customerInfo.selectedBranch.lng
          );
        }

        setRoadDistance(distance);
        setAllBranchDistances((prev) => ({
          ...prev,
          [currentBranchId]: distance,
        }));
      } catch {
        setRoadDistance(
          getDistance(
            userCoords.lat,
            userCoords.lng,
            customerInfo.selectedBranch.lat,
            customerInfo.selectedBranch.lng
          )
        );
      } finally {
        setIsCalculatingDistance(false);
      }
    };

    calculateDrivingDistance();
  }, [userCoords, customerInfo.selectedBranch.id]);

  <HelmetProvider>
    <App />
  </HelmetProvider>;

  const handleNavigate = (id: string, branchId?: number) => {
    setView(id);
    setTargetBranchId(branchId);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  const addToCart = (item: MenuItem, selectedAddonsRaw: MenuItem[] = []) => {
    const processedAddons: CartAddon[] = selectedAddonsRaw.map((a) => ({
      ...a,
      quantity: 1,
    }));

    setCart((prev) => {
      const addonIds = [...processedAddons]
        .map((a) => a.id)
        .sort()
        .join("-");
      const uniqueKey = `${item.id}-${addonIds}`;

      const existing = prev.find((i) => {
        const iAddonIds = i.selectedAddons
          ? [...i.selectedAddons]
              .map((a) => a.id)
              .sort()
              .join("-")
          : "";
        return `${i.id}-${iAddonIds}` === uniqueKey;
      });

      if (existing)
        return prev.map((i) => {
          const iAddonIds = i.selectedAddons
            ? [...i.selectedAddons]
                .map((a) => a.id)
                .sort()
                .join("-")
            : "";
          return `${i.id}-${iAddonIds}` === uniqueKey
            ? { ...i, quantity: i.quantity + 1 }
            : i;
        });

      return [
        ...prev,
        {
          ...item,
          quantity: 1,
          selectedAddons:
            processedAddons.length > 0 ? processedAddons : undefined,
        },
      ];
    });
    setNotification({ type: "success", message: `${item.name} ditambahkan!` });
  };

  const calculateItemPrice = (item: CartItem) => {
    const mainTotal = item.priceNum * item.quantity;
    const addonsTotal = (item.selectedAddons || []).reduce(
      (sum, a) => sum + a.priceNum * a.quantity,
      0
    );
    return mainTotal + addonsTotal;
  };

  const cartTotal = cart.reduce((s, i) => s + calculateItemPrice(i), 0);
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const distanceToUse = roadDistance !== null ? roadDistance : 0;
  const isFreeShipping = userCoords !== null && distanceToUse <= 2;
  const finalShippingCost = isFreeShipping ? 0 : customerInfo.shippingCost;
  const grandTotal = cartTotal + finalShippingCost;

  const handleAutoLocation = async () => {
    if (!navigator.geolocation) {
      setNotification({
        type: "error",
        message: "Browser tidak mendukung GPS.",
      });
      return;
    }

    if (geocodeController.current) geocodeController.current.abort();
    geocodeController.current = new AbortController();

    setIsAddressLoading(true);
    setNotification({ type: "info", message: "Mencari titik lokasi Anda..." });

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
            { signal: geocodeController.current?.signal }
          );
          const data = await response.json();
          if (data && data.address) {
            const addr = data.address;
            let formattedAddr = addr.road ? `${addr.road}` : "";
            if (addr.house_number) formattedAddr += ` No. ${addr.house_number}`;
            const details = [];
            if (addr.hamlet) details.push(addr.hamlet);
            if (addr.village) details.push(addr.village);
            if (addr.suburb) details.push(addr.suburb);
            if (addr.city || addr.town || addr.county)
              details.push(addr.city || addr.town || addr.county);
            let textResult = formattedAddr
              ? `${formattedAddr}, ${details.join(", ")}`
              : data.display_name.split(", Indonesia")[0];
            const mapsLink = `https://www.google.com/maps?q=${latitude},${longitude}`;
            setCustomerInfo((prev) => ({
              ...prev,
              address: `${textResult}\n\n(Titik Maps: ${mapsLink})`,
            }));
            setNotification({
              type: "success",
              message: "Alamat berhasil ditemukan!",
            });
          }
        } catch (err: any) {
          if (err.name !== "AbortError")
            setNotification({
              type: "error",
              message: "Gagal mengambil data alamat.",
            });
        } finally {
          setIsAddressLoading(false);
          setTimeout(() => setNotification(null), 3000);
        }
      },
      () => {
        setIsAddressLoading(false);
        setNotification({ type: "error", message: "Gagal akses GPS." });
        setTimeout(() => setNotification(null), 3000);
      },
      { enableHighAccuracy: true }
    );
  };

  const handleConfirmOrder = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (customerInfo.deliveryMode === "antar") {
      if (cartTotal < 50000) {
        setNotification({
          type: "error",
          message: "Layanan antar minimal Rp 50.000",
        });
        return;
      }
    }

    const orderId = `H-PAD-${Math.random()
      .toString(36)
      .substr(2, 5)
      .toUpperCase()}`;
    // Pastikan kita mengambil data cabang yang PALING UPDATE dari state branches berdasarkan ID yang dipilih

    const currentBranch =
      branches.find((b) => b.id === customerInfo.selectedBranch.id) ||
      customerInfo.selectedBranch;

    const selectedPayment =
      PAYMENT_METHODS.find(
        (p) =>
          p.id ===
          (customerInfo.paymentChoice === "qris" ? "qris_all" : "bank_bsi")
      ) || PAYMENT_METHODS[0];

    let resolvedQRIS = selectedPayment.qrImage;
    if (selectedPayment.type === "qris" && selectedPayment.branchQRIS) {
      resolvedQRIS =
        selectedPayment.branchQRIS[customerInfo.selectedBranch.id] ||
        selectedPayment.qrImage;
    }

    let resolvedAccount = selectedPayment.accountNumber;
    if (selectedPayment.type === "bank" && selectedPayment.branchAccounts) {
      resolvedAccount =
        selectedPayment.branchAccounts[customerInfo.selectedBranch.id] ||
        selectedPayment.accountNumber;
    }

    let resolvedAccountName = selectedPayment.accountName;
    if (selectedPayment.type === "bank" && selectedPayment.branchAccountNames) {
      resolvedAccountName =
        selectedPayment.branchAccountNames[customerInfo.selectedBranch.id] ||
        "Hisana Fried Chicken";
    }

    const newOrder: OrderDetails = {
      id: orderId,
      customerName: customerInfo.name,
      phone: customerInfo.phone,
      address: customerInfo.address,
      items: [...cart],
      subtotal: cartTotal,
      shippingCost: finalShippingCost,
      tax: 0,
      total: grandTotal,
      date: new Date().toLocaleString("id-ID", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }),
      paymentMethod: selectedPayment,
      paymentChoice: customerInfo.paymentChoice as "qris" | "bank_bsi",
      orderType: "online",
      deliveryMode: customerInfo.deliveryMode,
      // GUNAKAN INI: Agar whatsappLink di struk berisi nomor dari Sheets
      selectedBranch: currentBranch,
      resolvedQRIS: resolvedQRIS,
      resolvedAccountNumber: resolvedAccount,
      resolvedAccountName: resolvedAccountName,
    };

    const WEB_APP_URL =
      "https://script.google.com/macros/s/AKfycbxlCEvg-vXba7gyyHnHJIk7_uWKIAHwMFu3mIfMP5p1yglyB-iODI3EumIy8SkhKRx8mg/exec";

    try {
      fetch(WEB_APP_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newOrder),
      });

      setGeneratedOrder(newOrder);
      setActiveReceipt(newOrder);
      setHistory((prev) => [newOrder, ...prev]);
      setCart([]);
      localStorage.removeItem("hisana_cart_storage");
      setShowCheckout(false);
      setNotification({ type: "success", message: "Pesanan berhasil dibuat!" });

      const existingHistory = JSON.parse(
        localStorage.getItem("hisana_history") || "[]"
      );
      localStorage.setItem(
        "hisana_history",
        JSON.stringify([newOrder, ...existingHistory])
      );

      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Gagal mengirim pesanan:", err);
      setNotification({
        type: "error",
        message: "Gagal mengirim pesanan ke sistem.",
      });
    }
  };

  const processLocationUpdate = async (coords: {
    lat: number;
    lng: number;
  }) => {
    setUserCoords(coords);

    // 1. Hitung jarak lurus (Haversine) untuk SEMUA cabang (Sangat Cepat & Offline)
    const branchesWithStraightDist = BRANCHES.map((branch) => ({
      ...branch,
      straightDist: getDistance(coords.lat, coords.lng, branch.lat, branch.lng),
    }));

    // 2. Urutkan dan ambil 3 yang paling dekat saja untuk dicek rute aslinya
    const topCandidates = [...branchesWithStraightDist]
      .sort((a, b) => a.straightDist - b.straightDist)
      .slice(0, 3);

    // 3. Proses hanya kandidat terpilih
    const results = await Promise.all(
      branchesWithStraightDist.map(async (branch) => {
        // Cek apakah cabang ini masuk dalam Top 3 Terdekat
        const isTopCandidate = topCandidates.some((c) => c.id === branch.id);

        // Kondisi A: Sangat dekat (< 1km) atau Bukan Top 3 -> Gunakan Estimasi (Hemat API)
        if (branch.straightDist < 1.0 || !isTopCandidate) {
          return {
            id: branch.id,
            dist: branch.straightDist * 1.2, // Factor 1.2 lebih aman untuk estimasi
            method: "estimasi",
          };
        }

        // Kondisi B: Masuk Top 3 dan jarak masuk akal (1km - 5km) -> Cek Rute Asli
        if (branch.straightDist <= 5.0) {
          try {
            const response = await fetch(
              `https://router.project-osrm.org/route/v1/driving/${coords.lng},${coords.lat};${branch.lng},${branch.lat}?overview=false`
            );
            const data = await response.json();
            if (data.routes?.[0]) {
              return {
                id: branch.id,
                dist: data.routes[0].distance / 1000,
                method: "rute",
              };
            }
          } catch (e) {
            // Fallback jika API gagal
          }
        }

        // Default fallback
        return {
          id: branch.id,
          dist: branch.straightDist * 1.3,
          method: "estimasi",
        };
      })
    );

    const distMap: { [key: number]: number } = {};
    results.forEach((r) => (distMap[r.id] = r.dist));
    setAllBranchDistances(distMap);

    const sorted = [...results].sort((a, b) => a.dist - b.dist);
    const nearest = BRANCHES.find((b) => b.id === sorted[0].id);

    if (nearest && !hasManuallySelectedBranch.current) {
      setCustomerInfo((p) => ({ ...p, selectedBranch: nearest }));
      setRoadDistance(sorted[0].dist);
    }
  };

  useEffect(() => {
    if (notification && !isLocating) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification, isLocating]);

  const handleRequestLocation = () => {
    if (!navigator.geolocation) {
      setNotification({
        type: "error",
        message: "Browser Anda tidak mendukung GPS.",
      });
      return;
    }

    setIsLocating(true);
    setNotification({
      type: "info",
      message: "Menyinkronkan GPS... Mohon izinkan akses lokasi.",
    });

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const newCoords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        await processLocationUpdate(newCoords);
        setIsLocating(false);
        setNotification({
          type: "success",
          message: "Lokasi berhasil dikunci!",
        });
      },
      (error) => {
        setIsLocating(false);
        let msg = "Gagal mendeteksi lokasi.";
        if (error.code === 1)
          msg =
            "Akses ditolak. Mohon aktifkan izin lokasi di pengaturan browser Anda.";
        if (error.code === 3)
          msg = "Waktu habis. Pastikan GPS HP Anda aktif dan coba lagi.";

        setNotification({ type: "error", message: msg });
      },
      {
        enableHighAccuracy: true, // Wajib true untuk mobile agar akurat
        timeout: 20000, // Ditambah jadi 20 detik
        maximumAge: 0,
      }
    );
  };

  const navItems = [
    { id: "beranda", label: "BERANDA" },
    { id: "menu", label: "MENU" },
    { id: "riwayat", label: "RIWAYAT" },
    { id: "galeri", label: "GALERI" },
    { id: "panduan", label: "PANDUAN" },
    { id: "aduan", label: "ADUAN" },
    { id: "lokasi", label: "LOKASI" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <nav
        className="fixed w-full z-50 transition-all duration-500 bg-[#ee1d23] py-2.5 sm:py-5 text-white shadow-lg"
        role="navigation"
        aria-label="Main Navigation"
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 md:px-12 flex justify-between items-center">
          <div
            className="flex items-center gap-2.5 sm:gap-4 cursor-pointer shrink-0"
            onClick={() => handleNavigate("beranda")}
            role="link"
            aria-label="Kembali ke Beranda"
          >
            <div className="w-10 h-10 sm:w-16 sm:h-16 flex items-center justify-center transition-transform active:scale-95">
              <img
                src={LOGO_URL}
                className="w-full h-full object-contain"
                alt="Logo Hisana Fried Chicken"
              />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-[13px] sm:text-xl font-serif font-bold tracking-tight">
                Hisana Fried Chicken Padang
              </span>
              <span className="text-[6px] sm:text-[10px] font-black uppercase tracking-[0.2em] opacity-100 mt-0.5">
                {RESTAURANT_INFO.subName}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-5">
            <div className="hidden lg:flex space-x-5 xl:space-x-8 items-center mr-4 xl:mr-6 lg:ml-10">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavigate(item.id)}
                  className={`text-xs font-black tracking-widest uppercase hover:text-amber-400 transition-all relative group ${
                    view === item.id ? "text-amber-400" : ""
                  }`}
                >
                  {item.label}
                  <span
                    className={`absolute -bottom-1 left-0 w-full h-0.5 bg-amber-400 transition-transform duration-300 transform ${
                      view === item.id
                        ? "scale-x-100"
                        : "scale-x-0 group-hover:scale-x-100"
                    }`}
                    aria-hidden="true"
                  ></span>
                </button>
              ))}
            </div>
            <button
              onClick={() => setIsCartOpen(true)}
              aria-label={`Keranjang Belanja (${cartCount} item)`}
              className="relative transition-all p-2.5 sm:px-4 sm:py-2.5 rounded-xl flex items-center gap-2 border border-white/20 bg-white/5 hover:bg-amber-400 hover:text-slate-900 group active:scale-90 min-h-[44px]"
            >
              <ShoppingBag size={18} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 bg-amber-400 text-slate-900 text-[10px] font-black w-5 h-5 flex items-center justify-center rounded-full border-2 border-[#ee1d23] shadow-md animate-scale-in">
                  {cartCount}
                </span>
              )}
            </button>
            <button
              onClick={() => handleNavigate("menu")}
              className="hidden sm:flex bg-white text-[#ee1d23] px-6 py-3 rounded-2xl items-center gap-3 hover:bg-amber-400 hover:text-white transition-all shadow-md min-h-[44px]"
            >
              <Truck size={16} aria-hidden="true" />
              <span className="text-xs font-black uppercase tracking-widest">
                PESAN SEKARANG
              </span>
            </button>
            <button
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Buka Menu"
              aria-expanded={mobileMenuOpen}
              className="lg:hidden p-2 active:bg-white/10 rounded-lg transition-colors min-h-[44px]"
            >
              <MenuIcon size={26} />
            </button>
          </div>
        </div>
      </nav>

      <div
        className={`fixed inset-0 z-[100] transition-all duration-500 ${
          mobileMenuOpen ? "visible" : "invisible"
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile Navigation Menu"
      >
        <div
          className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-500 ${
            mobileMenuOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setMobileMenuOpen(false)}
        ></div>
        <div
          className={`absolute right-0 top-0 h-full w-[85%] max-w-sm shadow-2xl transition-transform duration-500 transform ${
            mobileMenuOpen ? "translate-x-0" : "translate-x-full"
          } bg-[#ee1d23] text-white flex flex-col`}
        >
          <div className="p-6 border-b flex justify-between items-center border-white/10">
            <div className="flex items-center gap-3">
              <img
                src={LOGO_URL}
                className="h-10 w-auto rounded-lg shadow-lg"
                alt="Hisana Logo"
              />
              <div className="flex flex-col leading-none">
                <span className="text-sm font-serif font-bold">
                  Hisana Fried Chicken Padang
                </span>
                <span className="text-[8px] font-black uppercase tracking-widest opacity-70">
                  Besar & Enak
                </span>
              </div>
            </div>
            <button
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Tutup Menu"
              className="p-2 hover:bg-white/10 rounded-full transition-colors active:scale-90 min-h-[44px]"
            >
              <X size={28} />
            </button>
          </div>
          <div className="flex-grow overflow-y-auto p-6 flex flex-col gap-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavigate(item.id)}
                className={`text-2xl font-serif font-black text-left py-4 px-4 rounded-2xl transition-all flex items-center justify-between group active:bg-white/10 ${
                  view === item.id
                    ? "bg-amber-400 text-slate-900"
                    : "text-white hover:bg-white/5"
                }`}
              >
                {item.label.charAt(0) + item.label.slice(1).toLowerCase()}
                <ArrowRight
                  size={20}
                  className={`transition-transform ${
                    view === item.id
                      ? "translate-x-0"
                      : "-translate-x-4 opacity-0 group-hover:translate-x-0 group-hover:opacity-100"
                  }`}
                  aria-hidden="true"
                />
              </button>
            ))}
          </div>
        </div>
      </div>

      <main id="main-content" className="overflow-x-hidden">
        {/* 1. HALAMAN UTAMA (Di-load instan tanpa lazy load agar LCP bagus) */}
        {view === "beranda" && (
          <HomeView
            onNavigate={handleNavigate}
            onAddToCart={addToCart}
            onOpenOnlineModal={() => setShowOnlineModal(true)}
            onOpenDetail={(item) => {
              setViewingItem(item);
            }}
            userCoords={userCoords}
            setUserCoords={setUserCoords}
            onRequestLocation={handleRequestLocation}
            isLocating={isLocating}
          />
        )}
        {view === "menu" && (
          <MenuView
            onAddToCart={addToCart}
            onAddonsLoaded={setAddons}
            onOpenDetail={(item) => {
              setViewingItem(item);
            }}
            onCheckout={(i) => {
              addToCart(i);
              setIsCartOpen(true);
            }}
            isDark={false}
          />
        )}

        {/* 2. HALAMAN PENDUKUNG (Menggunakan Lazy Load agar file index.js menyusut drastis) */}
        <React.Suspense
          fallback={
            <div className="w-full py-20 flex flex-col items-center justify-center text-slate-400 font-bold text-xs uppercase tracking-widest gap-3">
              <div className="w-6 h-6 border-2 border-slate-300 border-t-[#ee1d23] rounded-full animate-spin"></div>
              <span>Memuat Halaman...</span>
            </div>
          }
        >
          {view === "riwayat" && (
            <HistoryView
              history={history}
              onViewDetails={(order) => setActiveReceipt(order)}
              onNavigate={handleNavigate}
              onClearHistory={() => setConfirmDelete(true)}
            />
          )}
          {view === "galeri" && <GaleriView />}
          {view === "panduan" && <PanduanView />}
          {view === "aduan" && <AduanView />}
          {view === "lokasi" && (
            <LocationView targetBranchId={targetBranchId} />
          )}
        </React.Suspense>

        <Footer onNavigate={handleNavigate} onOpenLegal={() => {}} />
      </main>

      {/* ORDER RECEIPT MODAL WITH LAZY LOAD */}
      {activeReceipt && (
        <React.Suspense
          fallback={
            <div className="fixed inset-0 bg-black/70 z-[200] flex flex-col items-center justify-center text-white font-black text-xs uppercase tracking-widest gap-3">
              <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Membuat Struk Pesanan...</span>
            </div>
          }
        >
          <OrderReceipt
            order={activeReceipt}
            onClose={() => setActiveReceipt(null)}
            patokan={customerInfo.patokan}
          />
        </React.Suspense>
      )}

      <ActiveOrderPreview
        count={cartCount}
        total={cartTotal}
        onClick={() => setIsCartOpen(true)}
      />

      {/* CHATBOT TRUE LAZY LOAD: Terbuka jika user sudah berinteraksi secara riil */}
      {loadChatBot && ChatBotRef.current && (
        <React.Suspense fallback={null}>
          <ChatBotRef.current
            setView={setView}
            isHidden={showCheckout || isCartOpen}
            hasFloatingCart={cartCount > 0}
            isMenuOpen={mobileMenuOpen}
          />
        </React.Suspense>
      )}

      {showOnlineModal && (
        <div
          className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-platform-title"
        >
          {/* Lebar max-w-md di mobile, max-w-2xl di desktop */}
          <div className="bg-white w-full max-w-md md:max-w-2xl rounded-[2.5rem] p-6 md:p-10 text-center animate-scale-in shadow-2xl flex flex-col max-h-[90vh]">
            {/* Bagian Header - Tetap Diam */}
            <div className="flex-shrink-0">
              <Bot
                size={48}
                className="mx-auto text-[#ee1d23] mb-4 md:size-16"
                aria-hidden="true"
              />
              <h3
                id="modal-platform-title"
                className="text-xl md:text-2xl font-black mb-2"
              >
                Pilih Platform Pemesanan
              </h3>
              <p className="text-slate-500 text-xs md:text-sm mb-6">
                Klik pada cabang terdekat dari lokasi Anda
              </p>
            </div>

            {/* Bagian List Cabang - Bisa di-scroll jika banyak */}
            <div className="overflow-y-auto pr-2 custom-scrollbar space-y-6">
              {/* Kelompok GoFood */}
              <div>
                <h4 className="text-[10px] font-bold text-red-500 uppercase tracking-widest mb-3 text-left px-2">
                  GoFood
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {BRANCHES.filter((branch) => branch.goFoodLink).map(
                    (branch) => (
                      <a
                        key={`gofood-${branch.id}`}
                        href={branch.goFoodLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group block w-full py-4 bg-[#EE2737] text-white rounded-2xl font-black text-xs uppercase shadow-md active:scale-95 hover:bg-red-700 transition-all text-center min-h-[56px] flex flex-col items-center justify-center"
                      >
                        <span>GOFOOD</span>
                        <span className="text-[10px] font-normal opacity-100 lowercase mt-1 px-2">
                          ({branch.name.replace("Hisana Fried Chicken ", "")})
                        </span>
                      </a>
                    )
                  )}
                </div>
              </div>

              {/* Kelompok ShopeeFood */}
              <div>
                <h4 className="text-[10px] font-bold text-orange-500 uppercase tracking-widest mb-3 text-left px-2">
                  ShopeeFood
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {BRANCHES.filter((branch) => branch.shopeeFoodLink).map(
                    (branch) => (
                      <a
                        key={`shopee-${branch.id}`}
                        href={branch.shopeeFoodLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group block w-full py-4 bg-[#EE4D2D] text-white rounded-2xl font-black text-xs uppercase shadow-md active:scale-95 hover:bg-orange-600 transition-all text-center min-h-[56px] flex flex-col items-center justify-center"
                      >
                        <span>SHOPEEFOOD</span>
                        <span className="text-[10px] font-normal opacity-100 lowercase mt-1 px-2">
                          ({branch.name.replace("Hisana Fried Chicken ", "")})
                        </span>
                      </a>
                    )
                  )}
                </div>
              </div>
            </div>

            {/* Bagian Footer - Tetap Diam */}
            <div className="flex-shrink-0 mt-6 border-t pt-4">
              <button
                onClick={() => setShowOnlineModal(false)}
                className="w-full py-2 text-slate-400 text-xs font-bold uppercase hover:text-[#ee1d23] transition-colors"
              >
                BATAL
              </button>
            </div>
          </div>
        </div>
      )}

      {isCartOpen && (
        <div
          className="fixed inset-0 z-[110] bg-black/60 flex justify-end"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cart-title"
        >
          <div
            className="absolute inset-0"
            onClick={() => setIsCartOpen(false)}
            aria-hidden="true"
          ></div>
          <div className="w-full sm:max-w-sm bg-white h-full shadow-2xl animate-slide-in-right p-8 flex flex-col relative">
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center gap-3">
                <ShoppingBag
                  className="text-[#ee1d23]"
                  size={24}
                  aria-hidden="true"
                />
                <h2 id="cart-title" className="text-xl font-bold">
                  Keranjang
                </h2>
              </div>
              <button
                onClick={() => setIsCartOpen(false)}
                aria-label="Tutup Keranjang"
                className="p-2 hover:bg-slate-100 rounded-full transition-colors min-h-[44px]"
              >
                <X />
              </button>
            </div>

            <div className="flex-grow overflow-y-auto space-y-4 no-scrollbar pb-6">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4 opacity-40">
                  <ShoppingBag size={64} aria-hidden="true" />
                  <p className="font-bold text-sm">Keranjang Anda kosong</p>
                </div>
              ) : (
                cart.map((item, cartIdx) => (
                  <div
                    key={`${item.id}-${cartIdx}`}
                    className="flex flex-col gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100 shadow-sm animate-fade-in-up"
                    aria-label={`Item: ${item.name}`}
                  >
                    <div className="flex gap-4">
                      <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0 shadow-md bg-white border border-slate-100">
                        <img
                          src={item.image}
                          className="w-full h-full object-cover"
                          alt={item.name}
                        />
                      </div>
                      <div className="flex-grow">
                        <p className="font-bold text-[11px] text-slate-800 leading-snug uppercase">
                          {item.name}
                        </p>

                        {item.selectedAddons &&
                          item.selectedAddons.length > 0 && (
                            <div className="mt-2 space-y-1.5 border-l-2 border-rose-200 pl-3">
                              {item.selectedAddons.map((a) => (
                                <div
                                  key={a.id}
                                  className="flex items-center justify-between"
                                >
                                  <span className="text-[9px] font-black text-[#ee1d23] italic uppercase tracking-tighter">
                                    + {a.name} ({a.quantity}x)
                                  </span>
                                  <div className="flex items-center gap-1.5 bg-white border border-slate-100 rounded-lg p-0.5">
                                    <button
                                      onClick={() => {
                                        setCart((p) =>
                                          p.map((it, idx) => {
                                            if (idx !== cartIdx) return it;
                                            const nextAddons =
                                              it.selectedAddons?.map((ad) =>
                                                ad.id === a.id
                                                  ? {
                                                      ...ad,
                                                      quantity: Math.max(
                                                        1,
                                                        ad.quantity - 1
                                                      ),
                                                    }
                                                  : ad
                                              );
                                            return {
                                              ...it,
                                              selectedAddons: nextAddons,
                                            };
                                          })
                                        );
                                      }}
                                      aria-label={`Kurangi jumlah ${a.name}`}
                                      className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-[#ee1d23] active:scale-90 transition-all"
                                    >
                                      <Minus size={12} strokeWidth={3} />
                                    </button>
                                    <span className="text-[10px] font-black text-slate-700 w-3 text-center">
                                      {a.quantity}
                                    </span>
                                    <button
                                      onClick={() => {
                                        setCart((p) =>
                                          p.map((it, idx) => {
                                            if (idx !== cartIdx) return it;
                                            const nextAddons =
                                              it.selectedAddons?.map((ad) =>
                                                ad.id === a.id
                                                  ? {
                                                      ...ad,
                                                      quantity: ad.quantity + 1,
                                                    }
                                                  : ad
                                              );
                                            return {
                                              ...it,
                                              selectedAddons: nextAddons,
                                            };
                                          })
                                        );
                                      }}
                                      aria-label={`Tambah jumlah ${a.name}`}
                                      className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-[#ee1d23] active:scale-90 transition-all"
                                    >
                                      <Plus size={12} strokeWidth={3} />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                        <div className="flex justify-between items-center mt-4">
                          <span className="text-[#ee1d23] font-black text-xs">
                            Rp {calculateItemPrice(item).toLocaleString()}
                          </span>
                          <div className="flex items-center bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                            <button
                              onClick={() =>
                                setCart((p) =>
                                  p.map((i, idx) =>
                                    idx === cartIdx
                                      ? {
                                          ...i,
                                          quantity: Math.max(1, i.quantity - 1),
                                        }
                                      : i
                                  )
                                )
                              }
                              aria-label={`Kurangi jumlah ${item.name}`}
                              className="p-2 hover:bg-slate-50 hover:text-[#ee1d23] transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center"
                            >
                              <Minus size={14} />
                            </button>
                            <span
                              className="w-8 text-center font-bold text-xs"
                              aria-label={`Jumlah: ${item.quantity}`}
                            >
                              {item.quantity}
                            </span>
                            <button
                              onClick={() =>
                                setCart((p) =>
                                  p.map((i, idx) =>
                                    idx === cartIdx
                                      ? { ...i, quantity: i.quantity + 1 }
                                      : i
                                  )
                                )
                              }
                              aria-label={`Tambah jumlah ${item.name}`}
                              className="p-2 hover:bg-slate-50 hover:text-[#ee1d23] transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center"
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          setCart((p) => p.filter((_, idx) => idx !== cartIdx))
                        }
                        aria-label={`Hapus ${item.name} dari keranjang`}
                        className="text-slate-400 hover:text-[#ee1d23] transition-colors self-start p-2 min-h-[44px]"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {cart.length > 0 && (
              <div className="pt-6 border-t mt-auto bg-white">
                <div className="flex justify-between mb-4">
                  <span className="font-bold text-slate-600">Subtotal</span>
                  <span className="font-black text-xl text-[#ee1d23]">
                    Rp {cartTotal.toLocaleString()}
                  </span>
                </div>
                <button
                  onClick={() => {
                    setIsCartOpen(false);
                    setShowCheckout(true);
                  }}
                  className="w-full py-5 bg-[#ee1d23] text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-[#d1191e] active:scale-95 transition-all min-h-[52px]"
                >
                  CHECKOUT PESANAN
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showCheckout && (
        <React.Suspense
          fallback={
            <div className="fixed inset-0 bg-black/70 z-[200] flex flex-col items-center justify-center text-white font-black text-xs uppercase tracking-widest gap-3">
              {/* Spinner loading yang halus menggunakan Tailwind */}
              <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Memuat Form Pengiriman...</span>
            </div>
          }
        >
          <DataPengiriman
            customerInfo={customerInfo}
            setCustomerInfo={setCustomerInfo}
            handleConfirmOrder={handleConfirmOrder}
            setShowCheckout={setShowCheckout}
            handleRequestLocation={handleRequestLocation}
            isLocating={isLocating}
            userCoords={userCoords}
            allBranchDistances={allBranchDistances}
            isAddressLoading={isAddressLoading}
            handleAutoLocation={handleAutoLocation}
            cartTotal={cartTotal}
            grandTotal={grandTotal}
            notification={notification}
            setNotification={setNotification}
            isMobile={isMobile}
            hasManuallySelectedBranch={hasManuallySelectedBranch}
          />
        </React.Suspense>
      )}

      {notification && (
        <div
          className="fixed top-0 left-0 w-full flex justify-center z-[9999] pointer-events-none pt-6 px-4"
          role="status"
          aria-live="polite"
        >
          <div
            className={`
      ${notification.type === "success" ? "bg-green-700" : "bg-[#ee1d23]"} 
      text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-4 border border-white/20 backdrop-blur-md animate-toast-slide-down pointer-events-auto max-w-sm w-full transition-colors duration-300`}
          >
            <div className="bg-white/20 w-10 h-10 rounded-full flex items-center justify-center shrink-0">
              {notification.type === "success" ? (
                <Check size={24} className="text-white" />
              ) : (
                <AlertCircle size={24} className="text-white" />
              )}
            </div>
            <div className="flex-grow">
              <p className="text-xs font-bold leading-snug">
                {notification.message}
              </p>
            </div>
            <button
              onClick={() => setNotification(null)}
              aria-label="Tutup notifikasi"
              className="p-1 hover:bg-white/20 rounded-lg transition-all min-h-[32px] min-w-[32px] flex items-center justify-center"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-[2rem] p-8 max-w-sm w-full shadow-2xl border-4 border-[#ee1d23]/10 scale-in-center">
            <div className="bg-rose-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-[#ee1d23]">
              <Trash2 size={32} />
            </div>

            <h3 className="text-lg font-black text-center text-slate-900 uppercase leading-tight mb-2">
              Hapus Riwayat?
            </h3>
            <p className="text-slate-500 text-center text-[11px] mb-8 font-bold uppercase tracking-wider">
              Data yang sudah dihapus tidak dapat dikembalikan.
            </p>

            <div className="grid grid-cols-2 gap-3">
              {/* Tombol Batal */}
              <button
                onClick={() => setConfirmDelete(false)}
                className="py-3 px-4 rounded-xl bg-slate-100 text-slate-500 text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-all"
              >
                Batal
              </button>

              {/* Tombol Yes/Hapus */}
              <button
                onClick={() => {
                  setHistory([]);
                  localStorage.removeItem("hisana_history");
                  setConfirmDelete(false);
                  // Munculkan notifikasi sukses setelah hapus
                  setNotification({
                    type: "success",
                    message: "RIWAYAT TELAH DIBERSIHKAN!",
                  });
                  setTimeout(() => setNotification(null), 3000);
                }}
                className="py-3 px-4 rounded-xl bg-[#ee1d23] text-white text-[10px] font-black uppercase tracking-widest shadow-lg shadow-rose-200 hover:scale-105 active:scale-95 transition-all"
              >
                Ya, Hapus
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ✅ TAMBAH DI SINI */}
      <style>
        {`
    @keyframes marquee {
      0% {
        transform: translateX(40%);
      }
      100% {
        transform: translateX(-100%);
      }
    }
  `}
      </style>
    </div>
  );
};

export default App;
